import { useEffect, useState, useMemo } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, Search, Users, Check } from "lucide-react";

/**
 * Modal to bulk-assign students to a course.
 * Props:
 *  - course: { id, name, ... }
 *  - onClose: () => void
 */
const CourseEnrollmentModal = ({ course, onClose }) => {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/admin/students"),
      api.get(`/admin/courses/${course.id}/enrollments`),
    ])
      .then(([sRes, eRes]) => {
        if (!mounted) return;
        const all = (sRes.data || []).filter((s) => s.active !== false);
        setStudents(all);
        setSelected(new Set(eRes.data.student_ids || []));
      })
      .catch(() => toast.error("Fehler beim Laden"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [course.id]);

  const filtered = useMemo(() => {
    if (!search) return students;
    const q = search.toLowerCase();
    return students.filter((s) =>
      [s.first_name, s.last_name, s.email, s.phone, s.parent_name]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [students, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAllFiltered = () => {
    const allFilteredSelected = filtered.every((s) => selected.has(s.id));
    const next = new Set(selected);
    if (allFilteredSelected) {
      filtered.forEach((s) => next.delete(s.id));
    } else {
      filtered.forEach((s) => next.add(s.id));
    }
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/courses/${course.id}/enrollments`, {
        student_ids: Array.from(selected),
      });
      toast.success(`${r.data.enrolled} Schüler eingeschrieben`);
      onClose(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unbekannter Fehler";
      toast.error(`Fehler beim Speichern: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="font-serif-display text-xl flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> {course.name} · Schüler zuweisen</div>
            <div className="text-xs text-muted-foreground mt-1">{selected.size} von {students.length} aktiven Schülern ausgewählt</div>
          </div>
          <button onClick={() => onClose(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 border-b border-border flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              placeholder="Schüler suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="enrollment-search"
              className="w-full bg-input/30 border border-border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <button
            onClick={toggleAllFiltered}
            data-testid="enrollment-toggle-all"
            className="text-xs px-3 py-2 rounded-md border border-border hover:border-primary"
          >
            {filtered.every((s) => selected.has(s.id)) && filtered.length > 0 ? "Alle abwählen" : "Alle auswählen"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-1.5" data-testid="enrollment-list">
          {loading && <div className="text-sm text-muted-foreground text-center py-12">Lädt…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-12">Keine Schüler gefunden.</div>
          )}
          {!loading && filtered.map((s) => {
            const isSel = selected.has(s.id);
            return (
              <label
                key={s.id}
                data-testid={`enrollment-row-${s.id}`}
                className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                  isSel ? "border-primary/50 bg-primary/5" : "border-border hover:border-muted-foreground"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggle(s.id)}
                  className="w-4 h-4 accent-[hsl(var(--primary))]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.first_name} {s.last_name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {s.parent_name ? `${s.parent_name} · ` : ""}{s.phone || s.email || "—"}
                  </div>
                </div>
                {isSel && <Check className="w-4 h-4 text-primary" />}
              </label>
            );
          })}
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-3">
          <button type="button" onClick={() => onClose(false)} className="px-5 py-2 text-sm border border-border rounded-full hover:border-muted-foreground">
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="enrollment-save-btn"
            className="px-6 py-2 text-sm rounded-full btn-gold disabled:opacity-50"
          >
            {saving ? "Speichert…" : `${selected.size} Schüler einschreiben`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseEnrollmentModal;
